// src/components/Browser/ChainSwitchModal.js
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt, FaExclamationTriangle, FaGlobe } from 'react-icons/fa';
import { NetworkContext } from '../../contexts/NetworkContext';
import { getChainLogo } from '../../pages/Wallet/chainLogos';
import FaviconImage from './FaviconImage';
import './ChainSwitchModal.css';

const ChainSwitchModal = ({
  isOpen,
  onClose,
  chainId,
  onApprove,
  onReject,
  isLoading,
  site
}) => {
  const { t } = useTranslation();
  const { availableNetworks, currentChain } = useContext(NetworkContext);
  const [targetChain, setTargetChain] = useState(null);
  
  // Find target chain based on chainId
  useEffect(() => {
    if (!chainId) return;
    
    const chainKeys = Object.keys(availableNetworks || {});
    const foundChain = chainKeys.find(key => 
      availableNetworks[key].chainId === chainId
    );
    
    if (foundChain) {
      setTargetChain(availableNetworks[foundChain]);
    } else {
      setTargetChain(null);
    }
  }, [chainId, availableNetworks]);
  
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onReject();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onReject]);
  
  const handleApprove = () => {
    if (!targetChain) return;
    onApprove(targetChain.key);
  };
  
  if (!isOpen) return null;
  
  // Format hostname
  const hostname = site ? new URL(site).hostname : '';
  
  // Handle modal click to prevent overlay click propagation
  const handleModalClick = (e) => {
    e.stopPropagation();
  };
  
  return (
    <div className="chain-switch-modal-overlay" onClick={onReject}>
      <div className="chain-switch-modal" onClick={handleModalClick}>
        <h2>{t('browser.chainSwitchRequest.title', 'Network Switch Request')}</h2>
        
        <div className="site-info">
          <div className="site-favicon">
            <FaviconImage hostname={hostname} className="site-favicon-img" alt={hostname} />
          </div>
          <div className="site-details">
            <h3>{hostname}</h3>
            <p className="site-url">{site}</p>
          </div>
        </div>
        
        <div className="chain-switch-warning">
          <FaExclamationTriangle className="warning-icon" />
          <p>{t('browser.chainSwitchRequest.warning', 'This site is requesting to switch networks')}</p>
        </div>
        
        <div className="chain-switch-details">
          <div className="current-chain">
            <h4>{t('browser.chainSwitchRequest.currentNetwork', 'Current Network')}</h4>
            <div className="network-item">
              <div className="network-icon">
                <img 
                  src={getChainLogo(currentChain?.key)} 
                  alt={currentChain?.name} 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/wallet-logo.png';
                  }}
                />
              </div>
              <span className="network-name">{currentChain?.name}</span>
            </div>
          </div>
          
          <div className="switch-arrow">
            <FaExchangeAlt />
          </div>
          
          <div className="target-chain">
            <h4>{t('browser.chainSwitchRequest.targetNetwork', 'Target Network')}</h4>
            {targetChain ? (
              <div className="network-item">
                <div className="network-icon">
                  <img 
                    src={getChainLogo(targetChain?.key)} 
                    alt={targetChain?.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/assets/wallet-logo.png';
                    }}
                  />
                </div>
                <span className="network-name">{targetChain?.name}</span>
              </div>
            ) : (
              <div className="unknown-network">
                <FaGlobe className="unknown-icon" />
                <span>{t('browser.chainSwitchRequest.unknownNetwork', 'Unknown Network')} (ID: {chainId?.toString(16)})</span>
              </div>
            )}
          </div>
        </div>
        
        {!targetChain && (
          <div className="chain-switch-error">
            <FaExclamationTriangle className="error-icon" />
            <p>{t('browser.chainSwitchRequest.unsupportedNetwork', 'The requested network is not supported by this wallet')}</p>
          </div>
        )}
        
        <div className="chain-switch-actions">
          <button 
            className="reject-button"
            onClick={onReject}
            disabled={isLoading}
          >
            {t('browser.chainSwitchRequest.reject', 'Reject')}
          </button>
          <button 
            className="approve-button"
            onClick={handleApprove}
            disabled={isLoading || !targetChain}
          >
            {isLoading ? (
              <span className="loading-spinner"></span>
            ) : null}
            {t('browser.chainSwitchRequest.confirm', 'Switch Network')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChainSwitchModal;